/* Curated Men's collection: one product/variant source, no shared-store writes. */
(() => {
  'use strict';
  const COLOR = 'filter.v.option.color';
  const MIN = 'filter.v.price.gte';
  const MAX = 'filter.v.price.lte';
  // Keep the legacy URL value recognizable so its Featured fallback is explicit.
  const SORTS = ['manual', 'most-relevant', 'best-selling', 'price-ascending', 'price-descending', 'created-ascending', 'created-descending'];
  const groupValue = (value) => /^gid:\/\/shopify\/FilterSettingGroup\/\d+$/.test(value);
  const priceError = code => Object.assign(new Error(code), { code });
  function cents(value) {
    if (value == null || String(value).trim() === '') return null;
    const normalized = String(value).trim().replace(',', '.');
    if (!/^\d+(\.\d{1,2})?$/.test(normalized)) throw priceError('invalidPrice');
    const result = Math.round(Number(normalized) * 100);
    if (!Number.isSafeInteger(result)) throw priceError('smallerPrice');
    return result;
  }
  function readState(url) {
    const params = new URL(url).searchParams;
    const min = cents(params.get(MIN));
    const max = cents(params.get(MAX));
    if (min !== null && max !== null && min > max) throw priceError('invalidRange');
    const requested = params.get('sort_by') || 'manual';
    return {
      colors: [...new Set(params.getAll(COLOR).flatMap(value => value.split(',')).filter(Boolean))],
      min, max, sort: SORTS.includes(requested) ? requested : 'manual',
      unsupported: [...params.keys()].some(key => key.startsWith('filter.') && ![COLOR, MIN, MAX].includes(key)),
      unknownSort: !SORTS.includes(requested)
    };
  }
  function stateURL(base, state) {
    const url = new URL(base);
    [...url.searchParams.keys()].forEach(key => {
      if ([COLOR, MIN, MAX, 'page'].includes(key) || (!state.unsupported && key.startsWith('filter.'))) url.searchParams.delete(key);
    });
    state.colors.forEach(color => url.searchParams.append(COLOR, color));
    if (state.min !== null) url.searchParams.set(MIN, String(state.min / 100));
    if (state.max !== null) url.searchParams.set(MAX, String(state.max / 100));
    url.searchParams.set('sort_by', state.sort);
    return url;
  }
  function matchingVariants(product, state, grouped = new Set()) {
    const localColors = state.colors.filter(value => !groupValue(value)).map(value => value.toLowerCase());
    return product.variants.filter(variant =>
      (!state.colors.length || localColors.includes(variant.color.toLowerCase()) || grouped.has(variant.id)) &&
      (state.min === null || variant.price >= state.min) &&
      (state.max === null || variant.price <= state.max));
  }
  function results(products, state, grouped = new Set(), ranking = null) {
    const rows = products.flatMap(product => {
      const variants = matchingVariants(product, state, grouped);
      if (!variants.length) return [];
      const selected = variants.find(variant => variant.id === product.selectedId) || variants[0];
      return [{ product, variants, selected, price: selected.price }];
    });
    rows.sort((a, b) => {
      let delta = 0;
      if (state.sort === 'price-ascending') delta = a.price - b.price;
      if (state.sort === 'price-descending') delta = b.price - a.price;
      if (state.sort === 'created-ascending') delta = a.product.created - b.product.created;
      if (state.sort === 'created-descending') delta = b.product.created - a.product.created;
      if (state.sort === 'best-selling' && ranking) delta = ranking.indexOf(a.product.id) - ranking.indexOf(b.product.id);
      return delta || a.product.position - b.product.position;
    });
    return rows;
  }
  function validateSource(data, expectedIds, sort, groups) {
    if (!data || data.collection !== 'the-blanket-you-can-wear' || data.complete !== true || data.sort !== sort || !Array.isArray(data.products)) throw new Error('Incomplete collection source.');
    const ids = data.products.map(product => product.id);
    if (new Set(ids).size !== ids.length || ids.some(id => !expectedIds.includes(id))) throw new Error('Unexpected collection source.');
    if (!groups.length && expectedIds.some(id => !ids.includes(id))) throw new Error('A product is missing from the ranking source.');
    if (groups.some(group => !(data.activeColors || []).some(color => color.value === group))) throw new Error('A color group could not be resolved.');
    if (data.products.some(product => !Array.isArray(product.variants))) throw new Error('Missing variant matches.');
    return data;
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { cents, readState, stateURL, matchingVariants, results, validateSource };
  if (typeof window === 'undefined' || !window.customElements || customElements.get('mens-collection')) return;

  class MensCollection extends HTMLElement {
    text(key, values = {}) {
      return (this.dataset[key] || '').replace(/\[([A-Z_]+)\]/g, (token, name) =>
        Object.prototype.hasOwnProperty.call(values, name) ? String(values[name]) : token);
    }
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.generation = 0;
      this.cache = new Map();
      this.state = { colors: [], min: null, max: null, sort: 'manual' };
      this.products = [...this.querySelectorAll('[data-men-collection-card]')].map(element => {
        const variants = [...element.querySelectorAll('[data-men-card-swatch]')].filter(button => button.dataset.price !== '').map(button => ({
          id: button.dataset.variantId, color: button.dataset.color, price: Number(button.dataset.price), button
        })).filter(variant => Number.isFinite(variant.price) && variant.price >= 0);
        return { element, id: element.dataset.productId, position: Number(element.dataset.position), created: Number(element.dataset.created), variants, selectedId: variants[0]?.id };
      });
      this.grid = this.querySelector('[data-mens-grid]');
      this.form = this.querySelector('[data-mens-controls]');
      this.fieldset = this.querySelector('[data-mens-fieldset]');
      this.message = this.querySelector('[data-mens-message]');
      this.count = this.querySelector('[data-mens-count]');
      this.chips = this.querySelector('[data-mens-chips]');
      this.minInput = this.querySelector('[data-mens-min]');
      this.maxInput = this.querySelector('[data-mens-max]');
      this.sortInput = this.querySelector('[data-mens-sort]');
      this.colors = [...new Set(this.products.flatMap(product => product.variants.map(variant => variant.color)))];
      const colorOptions = this.querySelector('[data-mens-color-options]');
      this.colors.forEach(color => {
        const label = document.createElement('label');
        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox'; checkbox.name = COLOR; checkbox.value = color;
        label.append(checkbox, document.createTextNode(color));
        colorOptions.append(label);
      });
      const prices = this.products.flatMap(product => product.variants.map(variant => variant.price));
      this.maxInput.placeholder = String(Math.max(0, ...prices) / 100);
      this.onSubmit = event => { event.preventDefault(); this.fromForm(); };
      this.onChange = event => {
        if (event.target.matches('[data-mens-sort], input[type="checkbox"]')) this.fromForm();
      };
      this.onClick = event => this.clickControl(event);
      this.onHistory = () => this.fromURL();
      this.form.addEventListener('submit', this.onSubmit);
      this.form.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
      window.addEventListener('popstate', this.onHistory);
      this.fieldset.disabled = false;
      this.fromURL();
    }
    disconnectedCallback() {
      this.generation += 1;
      this.pending?.abort();
      this.form?.removeEventListener('submit', this.onSubmit);
      this.form?.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onClick);
      window.removeEventListener('popstate', this.onHistory);
      this.ready = false;
      this.querySelector('[data-mens-color-options]')?.replaceChildren();
    }
    fromURL() {
      try { this.apply(readState(location.href)); }
      catch (error) {
        this.generation += 1;
        this.pending?.abort();
        this.removeAttribute('aria-busy');
        const state = { colors: [], min: null, max: null, sort: 'manual' };
        this.state = state; this.syncControls(state); this.render(results(this.products, state), state, new Set(), new Map());
        this.message.textContent = this.text('errorAllProducts', { ERROR: this.text(error.code) });
      }
    }
    fromForm() {
      const url = new URL(location.href);
      [COLOR, MIN, MAX].forEach(key => url.searchParams.delete(key));
      const values = new FormData(this.form);
      for (const [key, value] of values) if ([COLOR, MIN, MAX, 'sort_by'].includes(key) && value !== '') url.searchParams.append(key, value);
      // FormData already contains the selected sort; remove the previous URL value.
      url.searchParams.set('sort_by', this.sortInput.value);
      // Keep native grouped-color selections until their chip is explicitly removed.
      (this.state?.colors || []).filter(groupValue).forEach(value => url.searchParams.append(COLOR, value));
      try {
        const state = readState(url);
        this.minInput.removeAttribute('aria-invalid'); this.maxInput.removeAttribute('aria-invalid');
        this.apply(state, 'push');
      } catch (error) {
        this.minInput.setAttribute('aria-invalid', 'true'); this.maxInput.setAttribute('aria-invalid', 'true');
        this.message.textContent = this.text('errorUnchanged', { ERROR: this.text(error.code) });
      }
    }
    syncControls(state) {
      this.form.querySelectorAll('input[type="checkbox"]').forEach(input => { input.checked = state.colors.includes(input.value); });
      this.minInput.value = state.min === null ? '' : state.min / 100;
      this.maxInput.value = state.max === null ? '' : state.max / 100;
      this.sortInput.value = state.sort === 'most-relevant' ? 'manual' : state.sort;
      this.minInput.removeAttribute('aria-invalid'); this.maxInput.removeAttribute('aria-invalid');
    }
    async source(sort, groups, signal) {
      const key = JSON.stringify([sort, groups]);
      if (this.cache.has(key)) return this.cache.get(key);
      const url = new URL(this.dataset.sourceUrl, location.origin);
      url.searchParams.set('section_id', this.dataset.sourceSection);
      url.searchParams.set('sort_by', sort);
      groups.forEach(group => url.searchParams.append(COLOR, group));
      if (window.Shopify?.theme?.role === 'unpublished') url.searchParams.set('preview_theme_id', String(window.Shopify.theme.id));
      const response = await fetch(url, { signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error('Collection source request failed.');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const node = doc.querySelector('[data-mens-source]');
      if (!node) throw new Error('Collection source missing.');
      const data = validateSource(JSON.parse(node.textContent), this.products.map(product => product.id), sort, groups);
      this.cache.set(key, data);
      return data;
    }
    async apply(state, historyMode) {
      const generation = ++this.generation;
      this.pending?.abort();
      const controller = new AbortController(); this.pending = controller;
      const timer = window.setTimeout(() => controller.abort(), 8000);
      const groups = state.colors.filter(groupValue);
      let grouped = new Set(), labels = new Map(), rank = null, appliedSort = state.sort;
      const messages = [];
      this.message.textContent = groups.length || state.sort === 'best-selling' ? this.text('updating') : '';
      this.setAttribute('aria-busy', 'true');
      try {
        if (groups.length) {
          const data = await this.source('manual', groups, controller.signal);
          grouped = new Set(data.products.flatMap(product => product.variants));
          labels = new Map(data.activeColors.map(color => [color.value, color.label]));
        }
        if (state.sort === 'best-selling') {
          try {
            const data = await this.source('best-selling', [], controller.signal);
            rank = data.products.map(product => product.id);
          } catch (error) {
            if (generation !== this.generation || !this.isConnected) return;
            appliedSort = 'manual';
            messages.push(this.text('bestSellingUnavailable'));
          }
        }
        if (state.sort === 'most-relevant') {
          appliedSort = 'manual';
          messages.push(this.text('relevanceUnavailable'));
        }
        if (generation !== this.generation || !this.isConnected) return;
        if (state.unsupported) messages.push(this.text('unsupportedFilter'));
        if (state.unknownSort) messages.push(this.text('unknownSort'));
        this.state = state;
        this.syncControls(state);
        const applied = { ...state, sort: appliedSort };
        const rows = results(this.products, applied, grouped, rank);
        this.render(rows, state, grouped, labels);
        this.message.textContent = messages.join(' ');
        this.dataset.appliedSort = appliedSort;
        if (historyMode) {
          const url = stateURL(location.href, state);
          if (url.href !== location.href) history[historyMode === 'replace' ? 'replaceState' : 'pushState']({}, '', url);
        }
      } catch (error) {
        if (generation !== this.generation || !this.isConnected) return;
        this.message.textContent = this.text('colorUnavailable');
        if (!this.state) {
          this.state = { colors: [], min: null, max: null, sort: 'manual' };
          this.syncControls(this.state);
          this.render(results(this.products, this.state), this.state, new Set(), new Map());
          this.message.textContent = this.text('colorUnavailableAll');
        } else this.syncControls(this.state);
      } finally {
        clearTimeout(timer);
        if (generation === this.generation) this.removeAttribute('aria-busy');
      }
    }
    render(rows, state, grouped, labels) {
      const focused = this.contains(document.activeElement) ? document.activeElement : null;
      const matching = new Set(rows.map(row => row.product.id));
      this.products.forEach(product => { product.element.hidden = !matching.has(product.id); });
      rows.forEach(row => {
        this.selectVariant(row.product, row.selected, row.variants);
        this.grid.append(row.product.element);
      });
      this.count.textContent = this.getAttribute(`data-count-${rows.length}`) || '';
      this.querySelector('[data-mens-empty]').hidden = rows.length !== 0;
      this.chips.replaceChildren();
      const chip = (text, key, value) => {
        const button = document.createElement('button'); button.type = 'button';
        button.dataset.mensRemove = key; button.dataset.value = value || '';
        button.textContent = text + ' ×'; button.setAttribute('aria-label', this.text('removeFilter', { FILTER: text }));
        this.chips.append(button);
      };
      state.colors.forEach(color => chip(labels.get(color) || color, 'color', color));
      if (state.min !== null || state.max !== null) {
        const money = value => new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: this.dataset.currency || 'USD' }).format(value / 100);
        chip((state.min === null ? money(0) : money(state.min)) + ' – ' + (state.max === null ? this.text('anyPrice') : money(state.max)), 'price');
      }
      const priceOnly = { ...state, colors: [] };
      this.form.querySelectorAll('input[type="checkbox"]').forEach(input => {
        const any = this.products.some(product => matchingVariants(product, { ...priceOnly, colors: [input.value] }).length);
        input.disabled = !any && !input.checked;
      });
      // Moving a card to its sorted position must not interrupt keyboard swatch use.
      if (focused?.matches('[data-men-card-swatch]') && !focused.disabled && !focused.closest('[data-men-collection-card]')?.hidden) focused.focus({ preventScroll: true });
    }
    selectVariant(product, variant, eligible) {
      product.selectedId = variant.id;
      const button = variant.button;
      const image = product.element.querySelector('[data-men-card-image]');
      if (image.getAttribute('src') !== button.dataset.imageSrc) {
        image.srcset = button.dataset.imageSrcset;
        image.src = button.dataset.imageSrc;
      }
      image.alt = button.dataset.imageAlt;
      product.element.querySelector('[data-men-card-color]').textContent = variant.color;
      product.element.querySelectorAll('[data-men-card-link]').forEach(link => {
        link.href = button.dataset.url;
        if (link.hasAttribute('aria-label')) link.setAttribute('aria-label', this.text('cardLabel', { PRODUCT: product.element.dataset.productTitle, COLOR: variant.color }));
      });
      const template = [...product.element.querySelectorAll('[data-men-price-for]')].find(node => node.dataset.menPriceFor === variant.id);
      if (template) product.element.querySelector('[data-men-card-price]').replaceChildren(template.content.cloneNode(true));
      const ids = new Set(eligible.map(item => item.id));
      product.variants.forEach(item => {
        item.button.setAttribute('aria-pressed', String(item.id === variant.id));
        item.button.disabled = !ids.has(item.id);
      });
    }
    clickControl(event) {
      const clear = event.target.closest('[data-mens-clear]');
      const remove = event.target.closest('[data-mens-remove]');
      const swatch = event.target.closest('[data-men-card-swatch]');
      if (clear) this.apply({ ...this.state, colors: [], min: null, max: null, unsupported: false }, 'push');
      if (remove && this.state) {
        const state = { ...this.state, colors: [...this.state.colors] };
        if (remove.dataset.mensRemove === 'price') state.min = state.max = null;
        else state.colors = state.colors.filter(color => color !== remove.dataset.value);
        this.apply(state, 'push');
      }
      if (swatch && !swatch.disabled) {
        const product = this.products.find(item => item.element.contains(swatch));
        if (product) { product.selectedId = swatch.dataset.variantId; this.apply(this.state); }
      }
    }
  }
  customElements.define('mens-collection', MensCollection);
})();

